import { useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuth as useAuthStore } from '@/lib/store'
import { DEMO_USER } from '@/lib/demoData'
import type { UserProfile } from '@/types'

export function useAuth() {
  const { user, isLoading, error, setUser, setLoading, setError, clearAuth } = useAuthStore()
  const navigate = useNavigate()
  const initialized = useRef(false)

  useEffect(() => {
    // Prevent double-initialization in StrictMode / re-renders
    if (initialized.current) return
    initialized.current = true

    if (isDemoMode) {
      // Demo: no Supabase — just mark as not loading and not authenticated
      setLoading(false)
      return
    }

    // -----------------------------------------------------------------------
    // Load initial session with guaranteed setLoading(false) via finally
    // -----------------------------------------------------------------------
    const init = async () => {
      try {
        setLoading(true)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) throw sessionError
        if (session?.user) {
          const { data, error: profileError } = await supabase
            .from('user_profiles')
            .select('*, company:companies(*)')
            .eq('id', session.user.id)
            .single()
          if (!profileError && data) setUser(data as UserProfile)
        }
      } catch (err) {
        console.error('[useAuth] session init error:', err)
      } finally {
        setLoading(false)
      }
    }

    init()

    // -----------------------------------------------------------------------
    // React to login / logout events
    // SIGNED_IN: fired after signInWithPassword — load profile + stop spinner
    // SIGNED_OUT: clear auth state
    // INITIAL_SESSION: skip — already handled by getSession() above
    // -----------------------------------------------------------------------
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          setLoading(true)
          const { data, error: profileError } = await supabase
            .from('user_profiles')
            .select('*, company:companies(*)')
            .eq('id', session.user.id)
            .single()
          if (!profileError && data) setUser(data as UserProfile)
        } catch (err) {
          console.error('[useAuth] profile load error:', err)
        } finally {
          setLoading(false)
        }
      } else if (event === 'SIGNED_OUT') {
        clearAuth()
      }
    })

    return () => { subscription.unsubscribe() }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setLoading(true)
    setError(null)

    if (isDemoMode) {
      await new Promise(r => setTimeout(r, 700))
      let demoUser = { ...DEMO_USER }
      if (email.toLowerCase().includes('admin'))       demoUser = { ...DEMO_USER, role: 'ADMIN', full_name: 'Admin Demo' }
      if (email.toLowerCase().includes('subcontrat'))  demoUser = { ...DEMO_USER, role: 'SUBCONTRATISTA', full_name: 'Subcontratista Demo' }
      if (email.toLowerCase().includes('cliente'))     demoUser = { ...DEMO_USER, role: 'CONSULTA_CLIENTE', full_name: 'Cliente Demo' }
      setUser(demoUser)
      setLoading(false)
      return true
    }

    const { error: authError } = await supabase.auth.signInWithPassword({ email, password })
    if (authError) {
      setError(authError.message === 'Invalid login credentials'
        ? 'Email o contraseña incorrectos.'
        : authError.message)
      setLoading(false)
      return false
    }
    // Success: onAuthStateChange(SIGNED_IN) will load the profile and call setLoading(false)
    return true
  }, [setLoading, setError, setUser])

  const logout = useCallback(async (): Promise<void> => {
    if (isDemoMode) {
      clearAuth()
      navigate('/login', { replace: true })
      return
    }
    setLoading(true)
    await supabase.auth.signOut()
    clearAuth()
    setLoading(false)
    navigate('/login', { replace: true })
  }, [setLoading, clearAuth, navigate])

  const updateProfile = useCallback(async (
    updates: Partial<Pick<UserProfile, 'full_name' | 'phone' | 'avatar_url'>>
  ): Promise<boolean> => {
    if (!user) return false
    if (isDemoMode) { setUser({ ...user, ...updates }); return true }
    const { error: updateError } = await supabase.from('user_profiles').update(updates).eq('id', user.id)
    if (updateError) { setError('No se pudo actualizar el perfil.'); return false }
    setUser({ ...user, ...updates })
    return true
  }, [user, setUser, setError])

  const inviteUser = useCallback(async (
    email: string,
    role: UserProfile['role'],
    companyId: string | null,
    fullName: string
  ): Promise<boolean> => {
    const normalizedRole = (user?.role ?? '').toLowerCase()
    if (normalizedRole !== 'admin') return false
    if (isDemoMode) return true
    const { error: fnError } = await supabase.functions.invoke('invite-user', {
      body: { email, role, fullName, companyId },
    })
    if (fnError) { setError(`No se pudo invitar al usuario: ${fnError.message}`); return false }
    return true
  }, [user, setError])

  // Role checks support both the legacy uppercase roles (ADMIN, SUPERVISOR_COBRA…)
  // and the lowercase roles used in the Navabuena org chart (admin, project_director…).
  const isAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'admin'
  const isSupervisor =
    isAdmin ||
    user?.role === 'SUPERVISOR_COBRA' ||
    user?.role === 'project_director' ||
    user?.role === 'construction_director' ||
    user?.role === 'area_manager' ||
    user?.role === 'supervisor'
  const isSubcontractor = user?.role === 'SUBCONTRATISTA'
  const isConsulta      = user?.role === 'CONSULTA_CLIENTE'
  const canEdit         = isAdmin || isSupervisor
  const canValidate     = isAdmin || isSupervisor
  const canExport       = isAdmin || isSupervisor

  return {
    user, isLoading, error, isAuthenticated: !!user,
    isAdmin, isSupervisor, isSubcontractor, isConsulta,
    canEdit, canValidate, canExport, isDemoMode,
    login, logout, updateProfile, inviteUser,
  }
}
